import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";

import { useContext, useEffect } from "react";
import { jsonLdScriptProps } from "react-schemaorg";

import CustomCard from "components/CustomCard";
import PaneLayout from "components/PaneLayout";
import SignUpForm from "components/SignUpForm";
import TextMore from "components/TextMore";
import { TopNavBarContext } from "components/TopNavBar/TopNavBarContext";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { useGlobalState } from "hooks/useGlobalState";
import useMediaQuery from "hooks/useMediaQuery";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import { emberAppBaseUrl } from "utils/envVariables";
import { jsonLDForProfile } from "utils/jsonLD";

import ProfilePageHeader from "./ProfilePageHeader";
import ProfilePageLayoutMobile from "./ProfilePageLayoutMobile";
import ProfilePageTabs from "./ProfilePageTabs";
import styles from "./style.module.scss";

const PremiumServiceWidget = dynamic(
  () => import("components/PremiumServiceWidget")
);

const DianomiAd = dynamic(() => import("components/DianomiAd"), {
  ssr: false,
});

type ProfilePageLayoutProps = {
  slug: string;
  children: React.ReactNode;
};

export const profileUrl = (slug: string, isCompany?: boolean) => {
  const url = new ApiEndpoint(
    `/api/v2/${isCompany ? "companies" : "accounts"}/${slug}`
  );
  const include = [];
  if (!isCompany) include.push("company,premium-service-catalogues");
  url.searchParams.append("include", include.join(","));
  // url.searchParams.append(
  //   "fields[accounts]",
  //   "slug,short-name,pretty-name,country,sector,entity-description,isin,unique-ticker,total-bullish,total-bearish,security,logo-url"
  // );
  url.searchParams.append("fields[premium-service-catalogues]", "title");
  url.searchParams.append("no-view-count", "true");
  return url.href;
};

const ProfilePageLayout = ({ slug, children }: ProfilePageLayoutProps) => {
  const { setShowBackButton } = useContext(TopNavBarContext);

  const media = useMediaQuery();
  const router = useRouter();
  const isCompany = router.route.startsWith("/companies/[slug]");
  const isPremiumServicesTab = router.route.endsWith("/premium-subscriptions");

  const { authenticated } = useAuthentication();

  const [signupSource, setSignupSource] = useGlobalState("signupSource");

  const { data: profile } = useApi(profileUrl(slug, isCompany), baseFetcher, {
    onSuccess: (data: any) => {
      setSignupSource({
        type: data.isCompany ? "Company" : "Account",
        id: data.id,
      });
    },
  });

  const coverImageUrl = profile?.avatarUrl?.xlarge;

  const canonicalUrl =
    emberAppBaseUrl + (router.asPath === "/" ? "" : router.asPath);

  useEffect(() => {
    setShowBackButton(false);
  }, [setShowBackButton]);

  useEffect(() => {
    if (profile && profile.slug !== slug) {
      router.replace(router.asPath.replace(`/${slug}/`, `/${profile.slug}/`));
    }
  }, [profile, router, slug]);

  useEffect(() => {
    if (
      profile &&
      signupSource?.id !== profile?.id &&
      (location.pathname.startsWith("/profiles") ||
        location.pathname.startsWith("/companies"))
    ) {
      setSignupSource({
        type: profile.isCompany ? "Company" : "Account",
        id: profile?.id,
      });
    }
  }, [profile, setSignupSource, signupSource?.id]);

  return (
    <>
      <Head>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />

        {/* Open graph tags for social media etc */}
        <meta property="og:image" content={coverImageUrl} />
        <meta property="og:image:width" content="200" />
        <meta property="og:image:height" content="200" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        {profile && (
          <script
            {...jsonLdScriptProps(jsonLDForProfile({ profile }) as any)}
          ></script>
        )}
      </Head>
      {media.mobile ? (
        <ProfilePageLayoutMobile
          profile={profile}
          slug={slug}
          isCompany={isCompany}
        >
          {children}
        </ProfilePageLayoutMobile>
      ) : (
        <PaneLayout
          className="px-[30px] py-[20px]"
          top={
            <CustomCard additionalStyle={{ marginBottom: "15px", padding: 0 }}>
              {profile && (
                <ProfilePageHeader {...{ profile: profile, isCompany }} />
              )}
            </CustomCard>
          }
          left={<ProfilePageTabs slug={slug} profile={profile} />}
          leftPaneClass="!min-w-[230px] !w-[230px]"
          right={
            <div className={styles.entityCards}>
              <DianomiAd type="right_sidebar" />
              {authenticated ? (
                <>
                  {profile?.hasPremiumServices && (
                    <CustomCard className={styles.card}>
                      <PremiumServiceWidget
                        profile={profile}
                        showCTA={isPremiumServicesTab}
                      />
                    </CustomCard>
                  )}
                </>
              ) : (
                !router.route.endsWith("/discussions") && (
                  <CustomCard className={styles.card}>
                    <SignUpForm
                      {...(isCompany
                        ? { companyId: profile?.id }
                        : { accountId: profile?.id })}
                      type="compact"
                    />
                  </CustomCard>
                )
              )}
              {profile?.summary && !isPremiumServicesTab && (
                <CustomCard className={styles.card}>
                  <div className={styles.descriptionCard}>
                    <h2>Summary</h2>
                    <h3 className="font-normal">
                      <TextMore text={profile?.summary} limit={800} />
                    </h3>
                  </div>
                </CustomCard>
              )}
            </div>
          }
        >
          <div className={styles.entityDetails}>{children}</div>
        </PaneLayout>
      )}
    </>
  );
};

export default ProfilePageLayout;
